"use client";

import { createContext, useContext, useEffect, useState, useRef, useCallback } from "react";
import { createSignalRConnection } from "../utils/signalrClient";

const SignalRContext = createContext(null);

// Status: "connecting" | "connected" | "reconnecting" | "disconnected"
export function SignalRProvider({ children }) {
  const [connection, setConnection] = useState(null);
  const [status, setStatus] = useState("connecting");
  const reconnectCallbacksRef = useRef(new Set());
  const connectionRef = useRef(null);

  // Pages call this to register a function that runs after reconnect
  const onReconnect = useCallback((callback) => {
    reconnectCallbacksRef.current.add(callback);
    return () => reconnectCallbacksRef.current.delete(callback);
  }, []);

  const fireReconnectCallbacks = () => {
    reconnectCallbacksRef.current.forEach((cb) => cb());
  };

  // Attach SignalR lifecycle events to a connection
  const attachEvents = (conn) => {
    conn.onreconnecting(() => setStatus("reconnecting"));
    conn.onreconnected(() => {
      setStatus("connected");
      fireReconnectCallbacks();
    });
    conn.onclose(() => {
      // Auto-reconnect gave up — user needs to click Reconnect
      setStatus("disconnected");
    });
  };

  // Connect (used on mount + Reconnect button)
  const connect = useCallback(async () => {
    // Stop existing connection if any
    if (connectionRef.current) {
      try { await connectionRef.current.stop(); } catch {}
    }

    setStatus("connecting");

    try {
      const conn = await createSignalRConnection();
      attachEvents(conn);
      connectionRef.current = conn;
      setConnection(conn);
      setStatus("connected");
      return true;
    } catch (err) {
      console.error("SignalR connection failed:", err);
      setStatus("disconnected");
      return false;
    }
  }, []);

  // Reconnect = connect + fire callbacks to refetch data
  const reconnect = useCallback(async () => {
    const success = await connect();
    if (success) fireReconnectCallbacks();
  }, [connect]);

  // Connect on mount
  useEffect(() => {
    connect();
    return () => {
      if (connectionRef.current) {
        connectionRef.current.stop().catch(() => {});
        connectionRef.current = null;
      }
    };
  }, []);

  return (
    <SignalRContext.Provider value={{ connection, status, onReconnect, reconnect }}>
      {children}
    </SignalRContext.Provider>
  );
}

export function useSignalR() {
  const context = useContext(SignalRContext);
  if (!context) throw new Error("useSignalR must be used within SignalRProvider");
  return context;
}